import type { PropsWithChildren } from '@shared/utils';
import { useCallback, useEffect, useRef } from 'react';

import { Box } from '../box';
import { Flex } from '../flex';

/*
 ********************* REMARK ABOUT THIS COMPONENT *********************
 * The `maxHeight` prop is used to set the maximum height of 'InfiniteScroll' component.
 * The `scrollableTarget` prop use to define an id of 'parent container' that is scrollable
 * maxHeight is not being used if the scrollableTarget is provided.
 */

type ScrollThresholdType = number | `${number}px`;
type InfiniteScrollMaxHeightType = `${number}px` | 'min-content' | 'fit-content' | `${number}dvh`;

export interface InfiniteScrollProps extends PropsWithChildren {
  hasMore: boolean;
  handleFetch: () => void | Promise<void>;
  loading: boolean;
  loader?: JSX.Element;
  scrollThreshold?: ScrollThresholdType;
  scrollableTarget?: string;
  maxHeight?: InfiniteScrollMaxHeightType;
}

const INFINITE_SCROLL_ID = 'infinite-scroll';

function isScrolledToBottom(
  scrollTop: number,
  clientHeight: number,
  scrollHeight: number,
  scrollThreshold: ScrollThresholdType,
) {
  if (typeof scrollThreshold === 'number') {
    return scrollTop + clientHeight >= scrollHeight * scrollThreshold;
  } else if (typeof scrollThreshold === 'string') {
    return scrollTop + clientHeight >= scrollHeight - parseInt(scrollThreshold);
  }

  return false;
}

export default function InfiniteScroll(props: Readonly<InfiniteScrollProps>) {
  const {
    hasMore,
    maxHeight,
    handleFetch,
    loading: isLoading,
    loader,
    scrollThreshold = 0.95,
    scrollableTarget,
    children,
  } = props;

  const isActionTriggeredRef = useRef(false);

  const getScrollableTarget = () => {
    return scrollableTarget
      ? document.getElementById(scrollableTarget)
      : document.getElementById(INFINITE_SCROLL_ID);
  };

  const handleScroll = useCallback(
    async (event: Event) => {
      const target = event.target as HTMLElement;
      const { scrollTop, scrollHeight, clientHeight } = target;

      const isAtBottom = isScrolledToBottom(scrollTop, clientHeight, scrollHeight, scrollThreshold);
      if (isAtBottom && !isLoading && hasMore && !isActionTriggeredRef.current) {
        isActionTriggeredRef.current = true;
        await handleFetch();
        isActionTriggeredRef.current = false;
      }
    },
    [isLoading, handleFetch, hasMore],
  );

  useEffect(() => {
    const scrollableElement = getScrollableTarget();
    if (scrollableElement) {
      scrollableElement.addEventListener('scroll', handleScroll);
      return () => {
        scrollableElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [handleScroll]);

  return (
    <Box>
      <Flex
        id={INFINITE_SCROLL_ID}
        sx={(theme) => ({
          flexDirection: 'column',
          height: maxHeight ?? 'auto',
          maxHeight: maxHeight,
          overflow: maxHeight ? 'scroll' : 'auto',
          rowGap: theme.spacing(2.5),
          mr: theme.spacing(-4.5),
          pr: theme.spacing(2),
          pb: hasMore ? theme.spacing(4) : 0,
          '--scrollbar-thumb-color': 'hsla(0, 0%, 0%, 0.1)',
          '--scrollbar-track-color': 'transparent',
          '@supports not selector(::-webkit-scrollbar)': {
            scrollbarWidth: 'thin',
            scrollbarColor: 'var(--scrollbar-thumb-color) var(--scrollbar-track-color)',
          },
          '@supports selector(::-webkit-scrollbar)': {
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: 'var(--scrollbar-thumb-color)',
              borderRadius: theme.shape.rounded(4),
              WebkitBoxShadow: '0 0 1px hsla(0, 0%, 100%, 0.5)',
              ':hover': {
                backgroundColor: 'hsla(0, 0%, 0%, 0.2)',
              },
            },
            '&::-webkit-scrollbar-track': {
              backgroundColor: 'var(--scrollbar-track-color)',
            },
          },
        })}
      >
        {children}
      </Flex>
      {isLoading && loader ? <Box>{loader}</Box> : null}
    </Box>
  );
}
